// When to load a pedigree, and what to do when the load fails (TGCLOUD-1618).
//
// Pure, so the two rules with real consequences are pinned by tests instead of only
// being reachable by mounting the whole chart in jsdom.
//
// THE BUG THESE EXIST FOR. The multi-pedigree workspace keeps every open tab
// MOUNTED, one PedigreeChartView each. Each fetched its family on mount and, on
// failure, called `navigate(pedigreeToolPath)` — a global router navigation. So a
// single tab the current user could not load (another tenant's family inherited
// through sessionStorage across a login, or one since deleted or merged) threw the
// whole app back to the family list, every time, from every pedigree. The reported
// symptom was "no pedigree will open, and a family I just created won't open
// either": it loaded fine and was then evicted by its stale neighbours.

// Whether this instance should fetch its family now.
//
// `hasBeenActive` latches: a tab loads when it first becomes the active one, not
// when it mounts. On entry to the pedigree route that is the difference between one
// full-family load and one per open tab — and each load carries `auditView=1`,
// which writes a Tier-2 "record opened" audit event, so eager loading recorded the
// user as having opened every tab when they opened one.
export const shouldFetchFamily = ({
  familyId,
  sessionInaccessible,
  hasBeenActive
}) => Boolean(familyId) && !sessionInaccessible && Boolean(hasBeenActive);

// Whether the pedigree route should (re)open a pedigree session for the family in
// the URL (TGCLOUD-1669 Phase 7).
//
// THE BUG THIS EXISTS FOR. A person tab carries the same familyId as its family's
// chart, and clicking any tab syncs the URL to that familyId so the route element
// stays mounted. The route effect then called openPedigree(routeFamilyId), which
// activates the PEDIGREE session for that family, so clicking a person tab
// activated the person and was immediately overridden back to the pedigree.
// Nothing a person tab renders is reachable without this guard.
//
// The rule is narrow on purpose: only skip when the ALREADY ACTIVE session is a
// person session for this very family. A deep link, a first visit, and a person
// tab for a DIFFERENT family all still open the pedigree, which is what the URL
// asked for.
//
// THE SECOND DEFECT, and why there is a clock in here. The guard above was too
// broad: it skipped whenever a person tab for the route family was active, and
// that state persists long after the tab click that created it. Every list that
// opens a family navigates by URL and does NOT call openPedigree — the family
// table, recent persons, recent submissions — so choosing that family from any
// list while one of its patients was the active tab changed the URL and left
// the patient record on screen. No chart, no error: the user pressed a family
// and nothing happened.
//
// The two cases are indistinguishable by state alone — both end with "route
// says family F, active session is a person of F" — so they are told apart by
// WHEN the activation happened. A tab click activates and navigates in the same
// turn (PedigreeTabBar.selectTab: setActive then navigate, batched into one
// render), so the activation is milliseconds old when this runs. A navigation
// from a list is a separate user action against a tab that was activated
// however long ago. Hence: an activation younger than the window below is the
// cause of this route change and must not be overridden; anything older means
// the URL is a fresh instruction and the pedigree opens.
//
// The window is generous relative to the gap it has to detect (one render
// commit, even with several heavy charts mounted) and small relative to the gap
// it must not swallow (leave the chart, find the family in a list, click it).
//
// `lastActiveAt` is stamped with Date.now() by the provider on both SET_ACTIVE
// (a tab click) and OPEN (single-clicking a node), so every activation this has
// to recognise carries one. A RESTORED session deliberately carries 0
// (workspacePersistence) — see the refresh case below.
export const TAB_ACTIVATION_WINDOW_MS = 2000;

// THE THIRD CASE: A BROWSER REFRESH ON A PERSON TAB.
//
// A restored session carries lastActiveAt 0, and 0 used to fall straight through
// to "explicit navigation", so pressing F5 on a patient record threw the
// clinician onto the family chart. The tab strip restored correctly; the tab
// they were reading did not.
//
// 0 alone cannot decide it. The list-navigation defect above reaches this
// function in exactly the same state — restored person tab of F active,
// lastActiveAt 0, route now F — when the user loads the app somewhere else,
// walks to a list and clicks that family. What separates them is not the
// session, it is WHERE THE DOCUMENT WAS LOADED: a refresh loaded the browser
// document at this family's own URL, and a list click did not. That is a fact
// about this page load, so it is read from the Navigation Timing entry (the
// document URL, not the current one, which React Router has already changed).
//
// It is also allowed to fire ONCE. Without that, a clinician who refreshed on a
// patient record, went to a list and clicked that same family would be refused
// the chart for the rest of the page load — the restored tab never gains a
// lastActiveAt until something activates it, and the load URL never changes.
// Consuming the allowance the moment it is used keeps it to the one route
// evaluation it is about: the page load's own.
//
// Both inputs are injectable so the rule stays testable as a pure function; the
// defaults are the only thing that touches the browser, and an environment
// without Navigation Timing reads '' and behaves exactly as before.
let restoredTabAllowanceUsed = false;

// Test seam, and the reset a new page load gets for free.
export const resetRestoredTabAllowance = () => {
  restoredTabAllowanceUsed = false;
};

const readDocumentLoadPath = () => {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    return nav?.name ? new URL(nav.name).pathname : '';
  } catch {
    // No Navigation Timing (jsdom, an old browser). Fail to the old behaviour.
    return '';
  }
};

// Whole-segment match: '/pedigree/G-1-01' names G-1-01, '/pedigree/G-1-010'
// does not.
const pathNamesFamily = (pathname, familyId) =>
  !!pathname &&
  !!familyId &&
  String(pathname)
    .split('/')
    .some((segment) => decodeURIComponent(segment) === String(familyId));

export const shouldOpenPedigreeForRoute = ({
  routeFamilyId,
  activeSession,
  now = Date.now(),
  documentLoadPath = readDocumentLoadPath()
} = {}) => {
  if (!routeFamilyId) return false;
  if (!activeSession) return true;
  const personTabForThisFamily =
    activeSession.kind === 'person' && activeSession.familyId === routeFamilyId;
  if (!personTabForThisFamily) return true;

  const activatedAt = Number(activeSession.lastActiveAt) || 0;
  if (activatedAt) {
    // Activated just now: THIS route change is that tab click. Don't fight it.
    if (now - activatedAt <= TAB_ACTIVATION_WINDOW_MS) return false;
    // An older activation plus a route change is an explicit navigation to the
    // family — from the family table, or from a recent-persons /
    // recent-submissions panel, none of which call openPedigree. Open the chart
    // the URL asked for.
    return true;
  }

  // No activation in this page load: a restored tab. It is the one the user was
  // reading only if the document itself was loaded at this family's URL, and
  // only for that first route evaluation.
  if (restoredTabAllowanceUsed) return true;
  if (!pathNamesFamily(documentLoadPath, routeFamilyId)) return true;
  restoredTabAllowanceUsed = true;
  return false;
};

// What a failed load should do. Returns the three effects separately so the caller
// stays a thin wiring layer and each rule is asserted on its own.
export const familyLoadFailureEffects = ({
  isPreview,
  workspaceMode,
  isActive,
  // 'pedigree' (the default) or 'person'. TGCLOUD-1669 Phase 7.
  sessionKind = 'pedigree'
}) => {
  // A read-only FHQ preview is not a tab and owns its own error handling.
  if (isPreview) {
    return {
      notifyPreview: true,
      markInaccessible: false,
      navigateToList: false
    };
  }
  return {
    notifyPreview: false,
    // Flag the session so it is never retried. Without this the tab re-fetches on
    // every visit to the pedigree route and fails again — the retry, not the first
    // failure, is what produced the lockout.
    markInaccessible: workspaceMode,
    // Only the ACTIVE tab may move the router. A hidden background tab that
    // navigates evicts the user from the pedigree they are actually looking at.
    // Outside the workspace (single chart) there is nothing to be background TO,
    // so the classic behaviour is unchanged.
    //
    // A PERSON tab never navigates, active or not. The list it would land on is
    // the pedigree list, which is not where someone who opened a patient record
    // was going; the inaccessible flag already tells them the tab is dead, and
    // it leaves their other tabs where they were.
    navigateToList: sessionKind !== 'person' && (!workspaceMode || isActive)
  };
};
